import React, { useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { products } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_TEL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_FACEBOOK_URL,
  CONTACT_WHATSAPP_URL
} from '../config/contact';
import './ProductDetail.css';

const FeatureIcon = ({ type }) => {
  if (type === 'orchard') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 22v-7M8 15c-2-3-2-7 4-10 6 3 6 7 4 10" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.8 5.7 21l2.3-7-6-4.6h7.6L12 2z" />
    </svg>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, formatPrice, t } = useLanguage();
  const { addToCart } = useCart();
  const reduceMotion = useReducedMotion();
  const tap = reduceMotion ? undefined : { scale: 0.97 };
  const product = products.find((p) => p.id === parseInt(id, 10));

  const bagSizes = useMemo(
    () => products.filter((p) => p.category === 'hazelnuts' && !p.comingSoon),
    []
  );

  const getProductName = (item = product) => {
    if (language === 'ro' && item?.nameRo) {
      return item.nameRo;
    }
    return item?.name || '';
  };

  const getDescription = () => {
    if (language === 'ro' && product?.descriptionRo) {
      return product.descriptionRo;
    }
    return product?.description || '';
  };

  if (!product) {
    return (
      <div className="product-detail">
        <div className="container">
          <p>{t('product.notFound')}</p>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="back-btn"
          >
            {t('product.backToProducts')}
          </button>
        </div>
      </div>
    );
  }

  const displayName = getProductName();
  const isComingSoon = Boolean(product.comingSoon);
  const showBagSizes = product.category === 'hazelnuts' && !isComingSoon;
  const canAddToCart =
    !isComingSoon && product.price != null && typeof product.price === 'number';
  const showHazelnutProvenance = product.category === 'hazelnuts' && !isComingSoon;

  const subjectProduct =
    product.weight && !displayName.includes(product.weight)
      ? `${displayName} — ${product.weight}`
      : displayName;
  const mailSubject = encodeURIComponent(
    t('product.orderEmailSubject').replace('{product}', subjectProduct)
  );
  const mailBody = encodeURIComponent(
    `${t('product.orderEmailBodyIntro')}\n${displayName}${product.weight ? ` (${product.weight})` : ''}\n`
  );
  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${mailSubject}&body=${mailBody}`;
  const whatsappHref = `${CONTACT_WHATSAPP_URL}?text=${encodeURIComponent(
    `${t('product.orderEmailBodyIntro')}\n${subjectProduct}`
  )}`;

  const weightLine = product.weight
    ? isComingSoon
      ? product.weight
      : `${product.weight} · ${t('product.juteBag')}`
    : null;

  return (
    <div className="product-detail">
      <div className="container">
        <motion.button
          type="button"
          onClick={() => navigate(-1)}
          className="back-btn"
          whileTap={tap}
        >
          {t('product.back')}
        </motion.button>

        <div className="product-detail-content">
          <div className="product-detail-image">
            {product.image ? (
              <img
                src={product.image}
                alt={displayName}
                className="product-detail-img"
              />
            ) : (
              <div className="product-placeholder-large">{product.name.charAt(0)}</div>
            )}
            {isComingSoon ? (
              <span className="product-badge coming-soon">{t('common.comingSoon')}</span>
            ) : (
              <>
                {product.bestseller && (
                  <span className="product-badge bestseller">{t('product.bestseller')}</span>
                )}
                {product.new && <span className="product-badge new">{t('product.new')}</span>}
              </>
            )}
          </div>

          <div className="product-detail-info">
            <h1>{displayName}</h1>
            {weightLine ? <p className="product-weight">{weightLine}</p> : null}

            {showBagSizes && (
              <div className="product-size-picker">
                <p className="product-size-label">{t('product.selectBagSize')}</p>
                <div className="product-size-pills" role="list">
                  {bagSizes.map((bag) => (
                    <Link
                      key={bag.id}
                      to={`/products/${bag.id}`}
                      role="listitem"
                      className={`product-size-pill${bag.id === product.id ? ' active' : ''}`}
                      aria-current={bag.id === product.id ? 'true' : undefined}
                    >
                      {bag.weight}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {!isComingSoon && (
              <div className="product-price-section">
                <span className="current-price">{formatPrice(product.price)}</span>
                {product.pricePerKg != null && (
                  <span className="product-per-kg">
                    {formatPrice(product.pricePerKg)} {t('product.perKg')}
                  </span>
                )}
              </div>
            )}

            <p className="product-description">{getDescription()}</p>

            {isComingSoon ? (
              <div className="coming-soon-banner" role="status">
                <strong>{t('common.comingSoon')}</strong>
                <p>{t('product.comingSoonNote')}</p>
              </div>
            ) : (
              <div className="product-order-actions">
                {canAddToCart && (
                  <motion.button
                    type="button"
                    className="add-to-cart-btn btn-primary"
                    onClick={() => addToCart(product)}
                    whileTap={tap}
                  >
                    {t('product.addToCart')}
                  </motion.button>
                )}
                <p className="order-message-label">{t('product.orderByMessage')}</p>
                <a
                  href={whatsappHref}
                  className="order-action-btn order-action-btn--primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('product.orderByWhatsApp')}
                </a>
                <a
                  href={CONTACT_FACEBOOK_URL}
                  className="order-action-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('product.orderByFacebook')}
                </a>
                <a href={mailtoHref} className="order-action-btn">
                  {t('product.orderByEmail')}
                </a>
                <a href={`tel:${CONTACT_PHONE_TEL}`} className="order-action-btn">
                  {t('product.orderByPhone')}: {CONTACT_PHONE_DISPLAY}
                </a>
              </div>
            )}

            {showHazelnutProvenance && (
              <div className="product-provenance">
                <h2 className="product-provenance-title">{t('product.provenanceTitle')}</h2>
                <ul className="product-provenance-list">
                  <li>{t('product.hazelnutCert')}</li>
                  <li>{t('product.hazelnutRaw')}</li>
                  <li>{t('product.hazelnutDelivery')}</li>
                  <li>{t('product.hazelnutPickup')}</li>
                  <li>{t('product.hazelnutSeller')}</li>
                </ul>
              </div>
            )}

            {!isComingSoon && !showHazelnutProvenance && (
              <div className="product-features">
                <div className="feature">
                  <span className="feature-icon"><FeatureIcon type="orchard" /></span>
                  <span>{t('product.freshFromOrchard')}</span>
                </div>
                <div className="feature">
                  <span className="feature-icon"><FeatureIcon type="quality" /></span>
                  <span>{t('product.premiumQuality')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
